import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
// @ts-ignore
import permissions from 'invitemember-permissions-model';
import {
	Permission,
	permissionGroups
} from './util';

type MemberPermissionsListProps = {
	data?: any;
	selectedPermissions: string[];
	onChangePermissions: (newPermissions: string[]) => void;
	size?: 'sm' | 'md';
};

const MemberPermissionsList: React.FC<MemberPermissionsListProps> = ({ data, selectedPermissions, onChangePermissions, size }) => {
	const [lockedPermissions, setLockedPermissions] = useState<string[]>([]);
	const { unpackPermission, unpackPermissions } = permissions;

	const handleCheckboxChange = (permissionKey: string) => {
		const isSelected = selectedPermissions.includes(permissionKey);

		let newSelectedPermissions = [...selectedPermissions];

		if (isSelected) {
			const permissionsToRemove = unpackPermission(permissionKey);
			newSelectedPermissions = newSelectedPermissions.filter(
				(key) => !permissionsToRemove.includes(key)
			);
		} else {
			const permissionsToAdd = unpackPermission(permissionKey);
			// @ts-ignore
			newSelectedPermissions = [...new Set([...newSelectedPermissions, ...permissionsToAdd])];
		}

		onChangePermissions(newSelectedPermissions);
		// @ts-ignore
		updateLockedPermissions(newSelectedPermissions);
	};

	const updateLockedPermissions = (selectedPerms: string[]) => {
		const newLockedPermissions = new Set<string>();

		selectedPerms.forEach((permissionKey) => {
			const permission = permissions[permissionKey];
			if (permission && /\bmanage\b/.test(permission.name.toLowerCase())) {
				const includedKeys = unpackPermission(permissionKey);
				includedKeys.forEach((key: any) => {
					if (key !== permissionKey) {
						newLockedPermissions.add(key);
					}
				});
			}
		});

		setLockedPermissions(Array.from(newLockedPermissions));
	};

	useEffect(() => {
		if (data) {
			const userPerms = data.userPermissions as Permission[];
			const userPermKeys = userPerms.map((perm: any) => perm.key);
			const unpackedPermissions: string[] = unpackPermissions(userPermKeys) as string[];

			let clearedPermissions: string[] = [...new Set(unpackedPermissions)];
			clearedPermissions = clearedPermissions.filter(
				(permission) => !permissions.assignableRoles.some((role: any) => role.key === permission)
			);
			onChangePermissions(clearedPermissions);
			updateLockedPermissions(clearedPermissions);
		}
	}, [data]);

	return (
		<div className="space-y-10">
			{Object.entries(permissionGroups).map(([sectionName, permissionKeys]) => (
				<div key={`${sectionName}`}>
					<h2 className="text-[14px]/[1.3] font-semibold pb-3 mb-4 border-b">{sectionName}</h2>
					<div className={classNames(
						'flex flex-row flex-wrap gap-x-0 md:gap-x-3',
						size === 'sm' && 'md:gap-x-3 gap-y-4',
						!size && 'gap-y-6',
					)}>
						{permissionKeys.map((key) => {
							const permission = permissions[key];

							if (permission) {
								const isSelected = selectedPermissions.includes(permission.key!);
								const isDisabled = lockedPermissions.includes(permission.key!);

								return (
									<div
										key={permission.key}
										className={classNames(
											'flex items-center pr-0 xxs:pr-2 md:pr-0',
											size === 'sm' && 'w-full xxs:w-1/2 md:w-[calc(33.33%-8px)]',
											!size && 'w-full md:w-1/6 md:min-w-[180px] xxs:w-1/2',
										)}
									>
										<label
											htmlFor={permission.key}
											className="relative flex items-center cursor-pointer select-none"
										>
											<input
												type="checkbox"
												id={permission.key}
												checked={isSelected}
												onChange={() => handleCheckboxChange(permission.key!)}
												disabled={isDisabled}
												className="sr-only"
											/>
											<div
												className={classNames(
													`min-w-5 w-5 h-5 mr-2 border rounded flex items-center justify-center`,
													isDisabled
														? 'bg-[#92B0B4] border-[#92B0B4]'
														: isSelected
															? 'bg-[#3CBBCE] border-[#3CBBCE]'
															: 'bg-white border-[#E8E8E8]'
												)}
											>
												{isSelected && (
													<svg
														width="12"
														height="8"
														viewBox="0 0 12 8"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															d="M1.5 3.48276L4.82609 7L10.5 1"
															stroke="white"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
												)}
											</div>
											<span className="text-[12px]/[1.2] md:text-[13px]/[1.4] mt-px">
                          {permission.name}
                        </span>
										</label>
									</div>
								);
							}
							return null;
						})}
					</div>
				</div>
			))}
		</div>
	);
}

export default MemberPermissionsList;
